import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts/dist/react-apexcharts.esm';
import { formatAmount } from '@panel/commons/helpers/formatAmount';
import SimpleTooltip from '@panel/commons/lib/Tooltip/SimpleTooltip';
import { useTranslation } from 'react-i18next';
import { selectColor, sendTrackingInfo } from '@panel/commons';
import { getColor } from '@panel/commons/core/themev3';
import { isBrasilSite } from '@panel/commons/helpers/siteUtils';
import emptyImageCharts from '../../assets/img/emptyStateCharts.svg';
import { exportToExcel } from '../dataExportAdapter';
import {
  ChartsWrapper,
  ChartsLabels,
  LabelStyled,
  LabelMarker,
  ChartContainer,
  ExportButtonContainer,
  ExportData
} from './styles';
import EmptyStateCharts from '../EmptyState';
import {
  monthFormat,
  monthFormatBrasil,
  daysOfWeeks,
  daysOfWeeksBrasil,
  newValueFormatted,
  newDateFormatted,
  getDaysBetweenDates,
  monthDateFormatted,
  dateFormattedPeriod,
  newPeriodFormatted,
  monthYearFormatted
} from '../../helpers/datesFormatHelper';
import './tooltipStyles.css';
import { showAnonymousLeads } from '../../helpers/showAnonymousLeads';

const Charts = ({
  chartType,
  setChartType,
  dataCharts,
  filter,
  startFilter,
  endFilter,
  device,
  isStatisticsByPosting,
  postingId,
  error,
  reloadDispatch
}) => {
  const { t } = useTranslation();
  const isVASite = process.env.REACT_APP_BUILD_SITE === 'VAMX';
  const daysBetweenDates = startFilter && endFilter && getDaysBetweenDates(startFilter, endFilter);
  const { impressions, views, userStat, period } = dataCharts;
  const hasAnonymousLeads = userStat && showAnonymousLeads(userStat.series);
  const [wasExported, setWasExported] = useState(false);

  const customPeriodLegend = days => {
    let textExit;
    if (days > 31 && days <= 180) {
      textExit = { text: t('panel.estadisticas.chart.legend.weeks'), offsetY: 112 };
      return textExit;
    }
    if (days > 180) {
      textExit = { text: t('panel.estadisticas.chart.legend.months'), offsetY: 92 };
      return textExit;
    }
    if (days <= 31) {
      textExit = { text: t('panel.estadisticas.chart.legend.days'), offsetY: 100 };
      return textExit;
    }
    return textExit;
  };

  const msgLegendChart = {
    firstPeriod: { text: t('panel.estadisticas.chart.legend.days'), offsetY: 95 },
    secondPeriod: { text: t('panel.estadisticas.chart.legend.days'), offsetY: 100 },
    thirdPeriod: { text: t('panel.estadisticas.chart.legend.weeks'), offsetY: 112 },
    customPeriod: customPeriodLegend(daysBetweenDates)
  };

  const stateChart1 = {
    series: [
      {
        name: t(`panel.estadisticas.generalData.title.impressions`),
        type: 'line',
        data: (impressions && impressions.series) || [],
        color: getColor('primary')
      }
    ],
    key: 'impressions',
    options: {
      states: {
        normal: {
          filter: {
            type: 'none',
            value: 1
          }
        },
        hover: {
          filter: {
            type: 'none',
            value: 1
          }
        },
        active: {
          allowMultipleDataPointsSelection: true,
          filter: {
            type: 'none',
            value: 0
          }
        }
      },
      fill: {
        colors: [selectColor('grayMedium')],
        opacity: 1,
        type: 'solid'
      },
      chart: {
        stacked: false, // Para visualizar las lineas apiladas en un mismo período
        background: 'white',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 600,
          animateGradually: {
            enabled: true,
            delay: 0
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        },
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      annotations: {
        position: 'front',
        xaxis: [
          {
            borderColor: 'transparent',
            label: {
              borderColor: 'transparent',
              text: msgLegendChart[filter].text,
              textAnchor: 'middle',
              position: 'bottom',
              orientation: 'horizontal',
              offsetX: 500,
              offsetY: msgLegendChart[filter].offsetY,
              style: {
                background: 'transparent',
                color: selectColor('grayDarkLight'),
                fontSize: '14px',
                fontWeight: 400,
                fontFamily: 'Hind'
              }
            }
          }
        ]
      },
      xaxis: {
        categories:
          filter === 'thirdPeriod' || (filter === 'customPeriod' && daysBetweenDates > 31 && daysBetweenDates <= 180)
            ? period
            : impressions && impressions.categories,
        tickPlacement: 'on',
        labels: {
          show: true,
          rotate: -45,
          rotateAlways: true,
          offsetX: impressions && impressions.categories && impressions.categories.length === 1 ? 255 : 2,
          offsetY: 0,
          style: {
            colors: selectColor('grayMediumDark'),
            fontSize: '12px',
            fontFamily: 'Hind',
            fontWeight: 500
          },
          formatter: value => {
            const valueFormated = newValueFormatted(value);
            // Evaluamos los casos para mostrar las fechas de los períodos seleccionados.
            let exitFormatted = [];
            if (value) {
              // Se muestra la fecha completa (05/12/2022) cuando se elige la opción de Últimos 7 días:
              if (filter === 'firstPeriod') {
                exitFormatted = valueFormated;
              }
              // Se muestra la fecha completa (05/12/2022) cuando se elige la opción de Últimos 30 días:
              if (filter === 'secondPeriod') {
                exitFormatted = valueFormated;
              }
              // Se muestran rangos por semana (01/10/2022 - 07/10/2022) cuando se elige la opción de últimos 90 días:
              if (filter === 'thirdPeriod') {
                exitFormatted = dateFormattedPeriod(value);
              }
              // Se muestra la fecha completa (05/12/2022) cuando se filtran datos custom <30 días:
              if (filter === 'customPeriod' && daysBetweenDates <= 31) {
                exitFormatted = valueFormated;
              }
              // Se muestran rangos por semana (01/10/2022 - 07/10/2022) cuando se filtran datos custom >30 y <=180 días:
              if (filter === 'customPeriod' && daysBetweenDates > 31 && daysBetweenDates <= 180) {
                exitFormatted = dateFormattedPeriod(value);
              }
              // Se muestra mes y año (12/2022) cuando se filtran datos custom >180 días:
              if (filter === 'customPeriod' && daysBetweenDates > 180) {
                exitFormatted = monthDateFormatted(valueFormated);
              }
            }
            return exitFormatted;
          }
        },
        axisBorder: {
          show: true,
          offsetX: -16,
          offsetY: 0
        },
        axisTicks: {
          show: false
        },
        tooltip: {
          enabled: false
        }
      },
      yaxis: {
        forceNiceScale: true,
        labels: {
          show: true,
          style: {
            colors: selectColor('grayMediumDark'),
            fontSize: '12px',
            fontFamily: 'Hind',
            fontWeight: 500
          },
          offsetX: 5,
          formatter: value => formatAmount(value)
        },
        axisBorder: {
          show: true,
          offsetX: -3
        },
        axisTicks: {
          show: false
        }
      },
      stroke: {
        show: true,
        width: [2],
        curve: 'straight',
        dashArray: [0, 3]
      },
      markers: {
        size: 5,
        strokeColors: [getColor('primary'), selectColor('grayMediumDark')],
        hover: {
          sizeOffset: 5
        }
      },
      grid: {
        show: true,
        borderColor: selectColor('grayMedium'),
        strokeDashArray: 5,
        position: 'back',
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        },
        padding: {
          top: 0,
          right: 30,
          bottom: 0,
          left: 30
        }
      },
      tooltip: {
        enabled: true,
        followCursor: true,
        shared: false,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const colorSerie = w.globals.markers.colors[seriesIndex];
          const color = colorSerie.replace('#', '');
          const date = w.globals.categoryLabels[dataPointIndex];
          const fechaFormat = filter !== 'thirdPeriod' ? date.toString().split('/') : null;
          const dayFormatter = newDateFormatted(date).getDay();
          const startDate = date[0]?.replace('-', '');
          const startDateFormat = startDate?.split('/');
          const endDate = date[1];
          const endDateFormat = endDate?.split('/');

          let tooltipOut = '';
          // Se formatea Tooltip para filtro de 90 días y períodos personalizados >30 y <=180 días:
          if (
            filter === 'thirdPeriod' ||
            (filter === 'customPeriod' && daysBetweenDates > 31 && daysBetweenDates <= 180)
          ) {
            tooltipOut = `<div class="arrow_box_hight" data-panel="estadisticas-chart-tooltip">
                  <div class="text_container">
                    Del ${
                      isBrasilSite
                        ? daysOfWeeksBrasil[newDateFormatted(startDate).getDay()].toString().toLowerCase()
                        : daysOfWeeks[newDateFormatted(startDate).getDay()].toString().toLowerCase()
                    } ${startDateFormat[0]} de ${
              isBrasilSite
                ? monthFormatBrasil[startDateFormat[1]].toLowerCase()
                : monthFormat[startDateFormat[1]].toLowerCase()
            } de ${startDateFormat[2]} <br/>
                        al ${
                          isBrasilSite
                            ? daysOfWeeksBrasil[newDateFormatted(endDate).getDay()].toString().toLowerCase()
                            : daysOfWeeks[newDateFormatted(endDate).getDay()].toString().toLowerCase()
                        } ${endDateFormat[0]} de ${
              isBrasilSite
                ? monthFormatBrasil[endDateFormat[1]].toLowerCase()
                : monthFormat[endDateFormat[1]].toLowerCase()
            } de ${endDateFormat[2]}
                    <br/>
                    <span class="marker_${color}">
                      ${w.globals.seriesNames[seriesIndex].replace('*', '')}: ${formatAmount(
              series[seriesIndex][dataPointIndex]
            )}
                    </span>
                  </div>
                </div>`;
            // Se formatea Tooltip para casos de períodos personalizados >180 días:
          } else if (filter === 'customPeriod' && daysBetweenDates > 180) {
            tooltipOut = `<div class="arrow_box" data-panel="estadisticas-chart-tooltip">
                  <div class="text_container">
                  ${isBrasilSite ? monthFormatBrasil[fechaFormat[0]] : monthFormat[fechaFormat[0]]} de ${fechaFormat[1]}
                  <br/>
                  <span class="marker_${color}">
                    ${w.globals.seriesNames[seriesIndex]}: ${formatAmount(series[seriesIndex][dataPointIndex])}
                  </span>
                  </div>
                </div>`;
          }
          // Se formatea Tooltip para filtro de 7, 30 días y períodos personalizados <=30 días:
          else {
            tooltipOut = `<div class="arrow_box" data-panel="estadisticas-chart-tooltip">
                  <div class="text_container">
                  ${isBrasilSite ? daysOfWeeksBrasil[dayFormatter] : daysOfWeeks[dayFormatter]}, ${fechaFormat[0]} de ${
              isBrasilSite ? monthFormatBrasil[fechaFormat[1]].toLowerCase() : monthFormat[fechaFormat[1]].toLowerCase()
            } de ${fechaFormat[2]}
                  <br/>
                  <span class="marker_${color}">
                    ${w.globals.seriesNames[seriesIndex]}: ${formatAmount(series[seriesIndex][dataPointIndex])}
                  </span>
                  </div>
                </div>`;
          }
          return tooltipOut;
        },
        onDatasetHover: {
          highlightDataSeries: false
        }
      },
      legend: {
        show: true,
        showForSingleSeries: true,
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '14px',
        fontFamily: 'Hind',
        fontWeight: 400,
        offsetY: -12,
        labels: {
          colors: 'black'
        },
        markers: {
          width: 11,
          height: 11,
          radius: 12,
          offsetX: -2,
          offsetY: -1,
          strokeWidth: 0
        },
        onItemClick: {
          toggleDataSeries: false
        },
        itemMargin: {
          horizontal: 15
        },
        onItemHover: {
          highlightDataSeries: false
        }
      }
    }
  };

  const stateChart2 = {
    series: [
      {
        name: t(`panel.estadisticas.generalData.title.visits`),
        type: 'line',
        data: (views && views.series) || [],
        color: getColor('primary')
      }
    ],
    key: 'visits',
    options: {
      states: {
        normal: {
          filter: {
            type: 'none',
            value: 1
          }
        },
        hover: {
          filter: {
            type: 'none',
            value: 1
          }
        },
        active: {
          allowMultipleDataPointsSelection: true,
          filter: {
            type: 'none',
            value: 0
          }
        }
      },
      fill: {
        colors: [selectColor('grayMedium')],
        opacity: 1,
        type: 'solid'
      },
      chart: {
        stacked: false, // Para visualizar las lineas apiladas en un mismo período
        background: 'white',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 600,
          animateGradually: {
            enabled: true,
            delay: 0
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        },
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      annotations: {
        position: 'front',
        xaxis: [
          {
            borderColor: 'transparent',
            label: {
              borderColor: 'transparent',
              text: msgLegendChart[filter].text,
              textAnchor: 'middle',
              position: 'bottom',
              orientation: 'horizontal',
              offsetX: 500,
              offsetY: msgLegendChart[filter].offsetY,
              style: {
                background: 'transparent',
                color: selectColor('grayDarkLight'),
                fontSize: '14px',
                fontWeight: 400,
                fontFamily: 'Hind'
              }
            }
          }
        ]
      },
      xaxis: {
        categories:
          filter === 'thirdPeriod' || (filter === 'customPeriod' && daysBetweenDates > 31 && daysBetweenDates <= 180)
            ? period
            : views && views.categories,
        tickPlacement: 'on',
        labels: {
          show: true,
          rotate: -45,
          rotateAlways: true,
          offsetX: views && views.categories && views.categories.length === 1 ? 255 : 2,
          offsetY: 0,
          style: {
            colors: selectColor('grayMediumDark'),
            fontSize: '12px',
            fontFamily: 'Hind',
            fontWeight: 500
          },
          formatter: value => {
            const valueFormated = newValueFormatted(value);
            // Evaluamos los casos para mostrar las fechas de los períodos seleccionados.
            let exitFormatted = [];
            if (value) {
              // Se muestra la fecha completa (05/12/2022) cuando se elige la opción de Últimos 7 días:
              if (filter === 'firstPeriod') {
                exitFormatted = valueFormated;
              }
              // Se muestra la fecha completa (05/12/2022) cuando se elige la opción de Últimos 30 días:
              if (filter === 'secondPeriod') {
                exitFormatted = valueFormated;
              }
              // Se muestran rangos por semana (01/10/2022 - 07/10/2022) cuando se elige la opción de últimos 90 días:
              if (filter === 'thirdPeriod') {
                exitFormatted = dateFormattedPeriod(value);
              }
              // Se muestra la fecha completa (05/12/2022) cuando se filtran datos custom <30 días:
              if (filter === 'customPeriod' && daysBetweenDates <= 31) {
                exitFormatted = valueFormated;
              }
              // Se muestran rangos por semana (01/10/2022 - 07/10/2022) cuando se filtran datos custom >30 y <=180 días:
              if (filter === 'customPeriod' && daysBetweenDates > 31 && daysBetweenDates <= 180) {
                exitFormatted = dateFormattedPeriod(value);
              }
              // Se muestra mes y año (12/2022) cuando se filtran datos custom >180 días:
              if (filter === 'customPeriod' && daysBetweenDates > 180) {
                exitFormatted = monthDateFormatted(valueFormated);
              }
            }
            return exitFormatted;
          }
        },
        axisBorder: {
          show: true,
          offsetX: -16,
          offsetY: 0
        },
        axisTicks: {
          show: false
        },
        tooltip: {
          enabled: false
        }
      },
      yaxis: {
        forceNiceScale: true,
        labels: {
          show: true,
          style: {
            colors: selectColor('grayMediumDark'),
            fontSize: '12px',
            fontFamily: 'Hind',
            fontWeight: 500
          },
          offsetX: 5,
          formatter: value => formatAmount(value)
        },
        axisBorder: {
          show: true,
          offsetX: -3
        },
        axisTicks: {
          show: false
        }
      },
      stroke: {
        show: true,
        width: [2],
        curve: 'straight',
        dashArray: [0, 3]
      },
      markers: {
        size: 5,
        strokeColors: [getColor('primary'), selectColor('grayMediumDark')],
        hover: {
          sizeOffset: 5
        }
      },
      grid: {
        show: true,
        borderColor: selectColor('grayMedium'),
        strokeDashArray: 5,
        position: 'back',
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        },
        padding: {
          top: 0,
          right: 30,
          bottom: 0,
          left: 30
        }
      },
      tooltip: {
        enabled: true,
        followCursor: true,
        shared: false,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const colorSerie = w.globals.markers.colors[seriesIndex];
          const color = colorSerie.replace('#', '');
          const date = w.globals.categoryLabels[dataPointIndex];
          const fechaFormat = filter !== 'thirdPeriod' ? date.toString().split('/') : null;
          const dayFormatter = newDateFormatted(date).getDay();
          const startDate = date[0]?.replace('-', '');
          const startDateFormat = startDate?.split('/');
          const endDate = date[1];
          const endDateFormat = endDate?.split('/');

          let tooltipOut = '';
          // Se formatea Tooltip para filtro de 90 días y períodos personalizados >30 y <=180 días:
          if (
            filter === 'thirdPeriod' ||
            (filter === 'customPeriod' && daysBetweenDates > 31 && daysBetweenDates <= 180)
          ) {
            tooltipOut = `<div class="arrow_box_hight" data-panel="estadisticas-chart-tooltip">
                  <div class="text_container">
                    Del ${
                      isBrasilSite
                        ? daysOfWeeksBrasil[newDateFormatted(startDate).getDay()].toString().toLowerCase()
                        : daysOfWeeks[newDateFormatted(startDate).getDay()].toString().toLowerCase()
                    } ${startDateFormat[0]} de ${
              isBrasilSite
                ? monthFormatBrasil[startDateFormat[1]].toLowerCase()
                : monthFormat[startDateFormat[1]].toLowerCase()
            } de ${startDateFormat[2]} <br/>
                        al ${
                          isBrasilSite
                            ? daysOfWeeksBrasil[newDateFormatted(endDate).getDay()].toString().toLowerCase()
                            : daysOfWeeks[newDateFormatted(endDate).getDay()].toString().toLowerCase()
                        } ${endDateFormat[0]} de ${
              isBrasilSite
                ? monthFormatBrasil[endDateFormat[1]].toLowerCase()
                : monthFormat[endDateFormat[1]].toLowerCase()
            } de ${endDateFormat[2]}
                    <br/>
                    <span class="marker_${color}">
                      ${w.globals.seriesNames[seriesIndex].replace('*', '')}: ${formatAmount(
              series[seriesIndex][dataPointIndex]
            )}
                    </span>
                  </div>
                </div>`;
            // Se formatea Tooltip para casos de períodos personalizados >180 días:
          } else if (filter === 'customPeriod' && daysBetweenDates > 180) {
            tooltipOut = `<div class="arrow_box" data-panel="estadisticas-chart-tooltip">
                  <div class="text_container">
                  ${isBrasilSite ? monthFormatBrasil[fechaFormat[0]] : monthFormat[fechaFormat[0]]} de ${fechaFormat[1]}
                  <br/>
                  <span class="marker_${color}">
                    ${w.globals.seriesNames[seriesIndex]}: ${formatAmount(series[seriesIndex][dataPointIndex])}
                  </span>
                  </div>
                </div>`;
          }
          // Se formatea Tooltip para filtro de 7, 30 días y períodos personalizados <=30 días:
          else {
            tooltipOut = `<div class="arrow_box" data-panel="estadisticas-chart-tooltip">
                  <div class="text_container">
                  ${isBrasilSite ? daysOfWeeksBrasil[dayFormatter] : daysOfWeeks[dayFormatter]}, ${fechaFormat[0]} de ${
              isBrasilSite ? monthFormatBrasil[fechaFormat[1]].toLowerCase() : monthFormat[fechaFormat[1]].toLowerCase()
            } de ${fechaFormat[2]}
                  <br/>
                  <span class="marker_${color}">
                    ${w.globals.seriesNames[seriesIndex]}: ${formatAmount(series[seriesIndex][dataPointIndex])}
                  </span>
                  </div>
                </div>`;
          }
          return tooltipOut;
        },
        onDatasetHover: {
          highlightDataSeries: false
        }
      },
      legend: {
        show: true,
        showForSingleSeries: true,
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '14px',
        fontFamily: 'Hind',
        fontWeight: 400,
        offsetY: -12,
        labels: {
          colors: 'black'
        },
        markers: {
          width: 11,
          height: 11,
          radius: 12,
          offsetX: -2,
          offsetY: -1,
          strokeWidth: 0
        },
        onItemClick: {
          toggleDataSeries: false
        },
        itemMargin: {
          horizontal: 15
        },
        onItemHover: {
          highlightDataSeries: false
        }
      }
    }
  };

  const stateChart3 = {
    series: [
      {
        name: t(`panel.estadisticas.generalData.chart.totalLeads${hasAnonymousLeads ? '.hasAnonymousLeads' : ''}`),
        type: 'line',
        data: (userStat && userStat.series && userStat.series.total) || [],
        color: isVASite ? selectColor('redVaMx') : getColor('primary'),
        yAxisIndex: 1
      },
      {
        name: t(`panel.estadisticas.generalData.chart.formLeads`),
        type: 'column',
        stack: 'leads',
        data: (userStat && userStat.series && userStat.series.leadForm) || [],
        color: selectColor(isVASite ? 'greenVaMx' : 'purple')
      },
      {
        name: t(`panel.estadisticas.generalData.chart.whatsappLeads`),
        type: 'column',
        stack: 'leads',
        data: (userStat && userStat.series && userStat.series.whatsapp) || [],
        color: selectColor(isVASite ? 'yellowVaMx' : 'purpleLight')
      },
      {
        name: t(`panel.estadisticas.generalData.chart.viewsLeads`),
        type: 'column',
        stack: 'leads',
        data: (userStat && userStat.series && userStat.series.viewData) || [],
        color: selectColor(isVASite ? 'blueVaMx' : 'blueLight')
      },
      ...(hasAnonymousLeads
        ? [
            {
              name: t(`panel.estadisticas.generalData.chart.anonymousLeads`),
              type: 'column',
              stack: 'leads',
              data: (userStat && userStat.series && userStat.series.anonymous) || [],
              color: selectColor('grayMediumLight')
            }
          ]
        : [])
    ],
    key: 'leads',
    options: {
      states: {
        normal: {
          filter: {
            type: 'none',
            value: 0.1
          }
        },
        hover: {
          filter: {
            type: 'darken',
            value: 0.6
          }
        },
        active: {
          allowMultipleDataPointsSelection: true,
          filter: {
            type: 'none',
            value: 0
          }
        }
      },
      fill: {
        colors: [selectColor('grayMedium')],
        opacity: 1,
        type: 'solid'
      },
      chart: {
        stacked: true, // Para visualizar las columnas apiladas en un mismo período
        stackType: 'normal',
        animations: {
          enabled: true,
          easing: 'easeout',
          speed: 500,
          animateGradually: {
            enabled: false,
            delay: 10
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        },
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      annotations: {
        position: 'front',
        xaxis: [
          {
            borderColor: 'transparent',
            label: {
              borderColor: 'transparent',
              text: msgLegendChart[filter].text,
              textAnchor: 'middle',
              position: 'bottom',
              orientation: 'horizontal',
              offsetX: 500,
              offsetY: msgLegendChart[filter].offsetY,
              style: {
                background: 'transparent',
                color: selectColor('grayDarkLight'),
                fontSize: '14px',
                fontWeight: 400,
                fontFamily: 'Hind'
              }
            }
          }
        ]
      },
      xaxis: {
        categories:
          filter === 'thirdPeriod' || (filter === 'customPeriod' && daysBetweenDates > 31 && daysBetweenDates <= 180)
            ? period
            : userStat && userStat.categories,
        tickPlacement: 'on',
        labels: {
          show: true,
          rotate: -45,
          rotateAlways: true,
          offsetX: userStat && userStat.categories && userStat.categories.length === 1 ? 240 : 2,
          offsetY: 0,
          style: {
            colors: selectColor('grayMediumDark'),
            fontSize: '12px',
            fontFamily: 'Hind',
            fontWeight: 500
          },
          formatter: value => {
            const valueFormated = newValueFormatted(value);
            // Evaluamos los casos para mostrar las fechas de los períodos seleccionados.
            let exitFormatted = [];
            if (value) {
              // Se muestra la fecha completa (05/12/2022) cuando se elige la opción de Últimos 7 días:
              if (filter === 'firstPeriod') {
                exitFormatted = valueFormated;
              }
              // Se muestra la fecha completa (05/12/2022) cuando se elige la opción de Últimos 30 días:
              if (filter === 'secondPeriod') {
                exitFormatted = valueFormated;
              }
              // Se muestran rangos por semana (01/10/2022 - 07/10/2022) cuando se elige la opción de últimos 90 días:
              if (filter === 'thirdPeriod') {
                exitFormatted = dateFormattedPeriod(value);
              }
              // Se muestra la fecha completa (05/12/2022) cuando se filtran datos custom <30 días:
              if (filter === 'customPeriod' && daysBetweenDates <= 31) {
                exitFormatted = valueFormated;
              }
              // Se muestran rangos por semana (01/10/2022 - 07/10/2022) cuando se filtran datos custom >30 y <=180 días:
              if (filter === 'customPeriod' && daysBetweenDates > 31 && daysBetweenDates <= 180) {
                exitFormatted = dateFormattedPeriod(value);
              }
              // Se muestra mes y año (12/2022) cuando se filtran datos custom >180 días:
              if (filter === 'customPeriod' && daysBetweenDates > 180) {
                exitFormatted = monthDateFormatted(valueFormated);
              }
            }
            return exitFormatted;
          }
        },
        axisBorder: {
          show: true,
          offsetX: -1
        },
        axisTicks: {
          show: false
        },
        tooltip: {
          enabled: false
        }
      },
      yaxis: [
        {
          // eje principal → columnas apiladas
          forceNiceScale: true,
          labels: {
            show: true,
            style: {
              colors: selectColor('grayMediumDark'),
              fontSize: '12px',
              fontFamily: 'Hind',
              fontWeight: 500
            },
            offsetX: -5,
            formatter: value => formatAmount(value)
          },
          axisBorder: {
            show: true,
            offsetX: -5,
            offsetY: -1
          },
          axisTicks: {
            show: false
          }
        },
        {
          // eje secundario → línea total
          opposite: true,
          show: true,
          forceNiceScale: true,
          labels: {
            show: false,
            formatter: value => formatAmount(value)
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          }
        }
      ],
      plotOptions: {
        bar: {
          columnWidth: userStat && userStat.categories && userStat.categories.length === 1 ? '40%' : '60%'
        }
      },
      stroke: {
        show: true,
        width: [2, 0, 0, 0, 0],
        curve: 'straight'
      },
      markers: {
        size: [5],
        strokeColors: isVASite ? selectColor('redVaMx') : getColor('primary'),
        hover: {
          sizeOffset: 5
        }
      },
      grid: {
        show: true,
        borderColor: selectColor('grayMedium'),
        strokeDashArray: 5,
        position: 'back',
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        },
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0
        }
      },
      tooltip: {
        enabled: true,
        followCursor: true,
        shared: false,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const colorSerie = w.globals.markers.colors[seriesIndex];
          const color = colorSerie.replace('#', '');
          const date = w.globals.categoryLabels[dataPointIndex];
          const fechaFormat = filter !== 'thirdPeriod' ? date.toString().split('/') : null;
          const dayFormatter = newDateFormatted(date).getDay();
          const startDate = date[0]?.replace('-', '');
          const startDateFormat = startDate?.split('/');
          const endDate = date[1];
          const endDateFormat = endDate?.split('/');

          let tooltipOut = '';
          // Se formatea Tooltip para filtro de 90 días y períodos personalizados >30 y <=180 días:
          if (
            filter === 'thirdPeriod' ||
            (filter === 'customPeriod' && daysBetweenDates > 31 && daysBetweenDates <= 180)
          ) {
            tooltipOut = `<div class="arrow_box_hight" data-panel="estadisticas-chart-tooltip">
                  <div class="text_container">
                    Del ${
                      isBrasilSite
                        ? daysOfWeeksBrasil[newDateFormatted(startDate).getDay()].toString().toLowerCase()
                        : daysOfWeeks[newDateFormatted(startDate).getDay()].toString().toLowerCase()
                    } ${startDateFormat[0]} de ${
              isBrasilSite
                ? monthFormatBrasil[startDateFormat[1]].toLowerCase()
                : monthFormat[startDateFormat[1]].toLowerCase()
            } de ${startDateFormat[2]} <br/>
                        al ${
                          isBrasilSite
                            ? daysOfWeeksBrasil[newDateFormatted(endDate).getDay()].toString().toLowerCase()
                            : daysOfWeeks[newDateFormatted(endDate).getDay()].toString().toLowerCase()
                        } ${endDateFormat[0]} de ${
              isBrasilSite
                ? monthFormatBrasil[endDateFormat[1]].toLowerCase()
                : monthFormat[endDateFormat[1]].toLowerCase()
            } de ${endDateFormat[2]}
                    <br/>
                    <span class="marker_${color}">
                      ${w.globals.seriesNames[seriesIndex].replace('*', '')}: ${formatAmount(
              series[seriesIndex][dataPointIndex]
            )}
                    </span>
                  </div>
                </div>`;
            // Se formatea Tooltip para casos de períodos personalizados >180 días:
          } else if (filter === 'customPeriod' && daysBetweenDates > 180) {
            tooltipOut = `<div class="arrow_box" data-panel="estadisticas-chart-tooltip">
                  <div class="text_container">
                  ${isBrasilSite ? monthFormatBrasil[fechaFormat[0]] : monthFormat[fechaFormat[0]]} de ${fechaFormat[1]}
                  <br/>
                  <span class="marker_${color}">
                    ${w.globals.seriesNames[seriesIndex]}: ${formatAmount(series[seriesIndex][dataPointIndex])}
                  </span>
                  </div>
                </div>`;
          }
          // Se formatea Tooltip para filtro de 7, 30 días y períodos personalizados <=30 días:
          else {
            tooltipOut = `<div class="arrow_box" data-panel="estadisticas-chart-tooltip">
                  <div class="text_container">
                  ${isBrasilSite ? daysOfWeeksBrasil[dayFormatter] : daysOfWeeks[dayFormatter]}, ${fechaFormat[0]} de ${
              isBrasilSite ? monthFormatBrasil[fechaFormat[1]].toLowerCase() : monthFormat[fechaFormat[1]].toLowerCase()
            } de ${fechaFormat[2]}
                  <br/>
                  <span class="marker_${color}">
                    ${w.globals.seriesNames[seriesIndex]}: ${formatAmount(series[seriesIndex][dataPointIndex])}
                  </span>
                  </div>
                </div>`;
          }
          return tooltipOut;
        },
        onDatasetHover: {
          highlightDataSeries: true
        }
      },
      legend: {
        show: true,
        position: 'bottom',
        showForSingleSeries: true,
        horizontalAlign: 'center',
        fontSize: '14px',
        fontFamily: 'Hind',
        fontWeight: 400,
        offsetY: -12,
        labels: {
          colors: 'black'
        },
        markers: {
          width: 11,
          height: 11,
          radius: 12,
          offsetX: -2,
          offsetY: -1,
          strokeWidth: 0
        },
        onItemClick: {
          toggleDataSeries: false
        },
        itemMargin: {
          horizontal: 15
        },
        onItemHover: {
          highlightDataSeries: false
        }
      }
    }
  };

  const [chartConfig, setChartConfig] = useState(null);
  const chartLabels = ['impressions', 'visits', 'leads'];
  const [exportData, setExportData] = useState([]);

  const dateRanges =
    filter === 'thirdPeriod' || (filter === 'customPeriod' && daysBetweenDates > 31 && daysBetweenDates <= 180)
      ? period
      : impressions && impressions.categories;

  // Aplicamos formato a los diferentes períodos que se van a exportar:
  const formatPeriod = date => {
    if (filter === 'thirdPeriod' || (filter === 'customPeriod' && daysBetweenDates > 31 && daysBetweenDates <= 180)) {
      return newPeriodFormatted(date);
    }
    if (filter === 'customPeriod' && daysBetweenDates > 180) return monthYearFormatted(date);
    return newValueFormatted(date);
  };

  // Se arma array de objetos con los datos a exportar:
  const adaptedExportData = dateRanges?.map((range, index) => {
    return {
      Período: formatPeriod(range),
      [t('panel.estadisticas.generalData.title.impressions')]: impressions && impressions.series[index],
      [t('panel.estadisticas.generalData.title.visits')]: views && views.series[index],
      [userStat && showAnonymousLeads(userStat.series)
        ? t('panel.estadisticas.generalData.chart.totalLeads.hasAnonymousLeads')
        : t('panel.estadisticas.generalData.chart.totalLeads')]:
        userStat && userStat.series && userStat.series.total[index],
      [t('panel.estadisticas.generalData.chart.formLeads')]:
        userStat && userStat.series && userStat.series.leadForm[index],
      [t('panel.estadisticas.generalData.chart.whatsappLeads')]:
        userStat && userStat.series && userStat.series.whatsapp[index],
      [t('panel.estadisticas.generalData.chart.viewsLeads')]:
        userStat && userStat.series && userStat.series.viewData[index],
      [userStat && showAnonymousLeads(userStat.series)
        ? t('panel.estadisticas.generalData.chart.anonymousLeads')
        : '']: showAnonymousLeads(userStat.series)
        ? userStat && userStat.series && userStat.series.anonymous[index]
        : ''
    };
  });

  // Se establece el nombre del archivo que se exporta:
  const fileName = isStatisticsByPosting ? `Rendimiento_período_${postingId}` : 'Rendimiento_período';

  useEffect(() => {
    if (chartType) {
      const config = () => {
        if (chartType === 'impressions') return stateChart1;
        if (chartType === 'visits') return stateChart2;
        return stateChart3;
      };
      setChartConfig(config());
      setExportData(adaptedExportData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartType, dataCharts]);

  useEffect(() => {
    setWasExported(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCharts]);

  return (
    <ChartsWrapper>
      <ChartsLabels>
        {chartLabels.map(label => {
          return (
            <LabelStyled
              isActive={chartType === label}
              onClick={() => {
                if (dataCharts) {
                  setChartType(label);
                }
                sendTrackingInfo(
                  'Panel',
                  'Estadisticas',
                  `Rendimiento-${t(`panel.estadisticas.sendTrackingInfo.${label}`)}`
                );
              }}
              disabled={!dataCharts}
              key={label}
              data-panel={`panel-estadisticas-labelChart-${label}`}
            >
              {t(`panel.estadisticas.generalData.title.${label}`)}
              <LabelMarker isActive={chartType === label} data-panel={`panel-estadisticas-labelMarker-${label}`} />
            </LabelStyled>
          );
        })}
        {dataCharts && !error && (
          <ExportButtonContainer>
            <SimpleTooltip
              isMobile={device === 'tablet' || device === 'mobile'}
              position='top-center'
              text={t(
                wasExported
                  ? 'panel.estadisticas.generalData.chart.exportData.exported'
                  : 'panel.estadisticas.generalData.chart.exportData'
              )}
              dataPanel='Button-Export-ExcelData-Tooltip'
            >
              <ExportData
                wasExported={wasExported}
                onClick={() => {
                  if (!wasExported) {
                    exportToExcel(exportData || [], fileName);
                    sendTrackingInfo('Panel', 'Estadisticas', 'Rendimiento-Descargar');
                    setWasExported(true);
                  }
                }}
                dataPanel='Button-Export-ExcelData'
              />
            </SimpleTooltip>
          </ExportButtonContainer>
        )}
      </ChartsLabels>
      {dataCharts && !error ? (
        <ChartContainer>
          {chartConfig && (
            <Chart
              options={chartConfig.options}
              series={chartConfig.series}
              key={chartConfig.key}
              width='100%'
              height='100%'
            />
          )}
        </ChartContainer>
      ) : (
        <EmptyStateCharts image={emptyImageCharts} callback={reloadDispatch} />
      )}
    </ChartsWrapper>
  );
};

export default Charts;
